import { DEV } from "solid-js"
import {
  Box3,
  Color,
  InstancedBufferAttribute,
  InstancedMesh,
  MeshLambertMaterial,
  PlaneGeometry,
  Sphere,
  Vector3,
  type Material
} from "three"
import type { Engine } from "../../../core/engine/Engine.type"
import { mountOnFrameUpdateToObject3D } from "../../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate"
import Unexpected from "../../../Exception/Unexpected.class"
import noop from "../../../ldsh/noop"
import { VERTICES_PER_TILE } from "../../consts"
import { dummyMatrix, dummyObject3D, resetDummyObject3D } from "../DummyObject3D/dummyObject3D"
import type { WaterCurveParticleData } from "./createWaterCurveParticleDataFromCurves"
import { estuaryParticlesFragmentShader, estuaryParticlesVertexShader } from "./estuaryParticlesShader"

const particleDirection = new Vector3()

export function createGPUDrivenEstuaryParticles(
  engine: Engine,
  particleData: Array<WaterCurveParticleData>,
  particleBoundingBoxPoints: Array<Vector3>,
) {
  // console.log('createGPUDrivenEstuaryParticles', particleData.length)
  const count = particleData.length
  const particleSize = 0.05
  const geometry = new PlaneGeometry(particleSize, particleSize)

  const mesh = new InstancedMesh(geometry, undefined as unknown as Material, count)

  resetDummyObject3D()

  // Attribute arrays
  const starts = new Float32Array(count * 3)
  const mids = new Float32Array(count * 3)
  const ends = new Float32Array(count * 3)
  const lifetimes = new Float32Array(count)
  const offsets = new Float32Array(count)

  const dValue = 3
  function distortion() {
    return ((Math.random() * dValue) - dValue) / 2
  }

  for (let i = 0; i < count; i++) {
    const p = particleData[i]
    const { start, mid, end, offset, lifetime } = p

    dummyObject3D.updateMatrix()
    mesh.setMatrixAt(i, dummyMatrix)

    particleDirection.copy(end).sub(start).normalize()

    const startArr = start.toArray()
    const midArr = mid.toArray()
    const endArr = end.toArray()

    // old version, written before calculating particleDirection
    // if (horz) {
    //   startArr[1] += distortion()
    //   midArr[1] += distortion()
    //   endArr[1] += distortion()
    // } else {
    //   startArr[0] += distortion()
    //   midArr[0] += distortion()
    //   endArr[0] += distortion()
    // }

    startArr[0] += distortion() * particleDirection.y
    startArr[1] += distortion() * particleDirection.x
    startArr[2] += distortion() * particleDirection.z
    midArr[0] += distortion() * particleDirection.y
    midArr[1] += distortion() * particleDirection.x
    midArr[2] += distortion() * particleDirection.z
    endArr[0] += distortion() * particleDirection.y
    endArr[1] += distortion() * particleDirection.x
    endArr[2] += distortion() * particleDirection.z

    starts.set(startArr, i * 3)
    mids.set(midArr, i * 3)
    ends.set(endArr, i * 3)
    lifetimes[i] = lifetime
    offsets[i] = offset
  }

  geometry.setAttribute("aStart", new InstancedBufferAttribute(starts, 3))
  geometry.setAttribute("aMid", new InstancedBufferAttribute(mids, 3))
  geometry.setAttribute("aEnd", new InstancedBufferAttribute(ends, 3))
  geometry.setAttribute("aLifetime", new InstancedBufferAttribute(lifetimes, 1))
  geometry.setAttribute("aOffset", new InstancedBufferAttribute(offsets, 1))

  const bounding = new Box3().setFromPoints(particleBoundingBoxPoints)
  bounding.expandByScalar(VERTICES_PER_TILE / 2)
  geometry.boundingBox = bounding
  mesh.boundingBox = bounding
  const boundingSphere = bounding.getBoundingSphere(new Sphere())
  geometry.boundingSphere = mesh.boundingSphere = boundingSphere
  if (DEV) {
    mesh.computeBoundingBox = geometry.computeBoundingBox = () => {
      throw new Unexpected('computeBoundingBox should not be called')
    }
  } else {
    mesh.computeBoundingBox = geometry.computeBoundingBox = noop
  }

  const material = new MeshLambertMaterial({
    color: new Color(0x55ccff),
    // color: new Color(0xff0000), // red for debugging
    // transparent: true,
    // opacity: 0.2,
    // depthWrite: false,
    // side: DoubleSide,
  })
  // material.alphaTest = 0.5
  // material.transparent = false

  const uTime = { value: 0 }

  material.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = uTime
    shader.vertexShader = estuaryParticlesVertexShader
    shader.fragmentShader = estuaryParticlesFragmentShader
  }

  mesh.material = material
  material.needsUpdate = true

  mountOnFrameUpdateToObject3D(engine, mesh, (dt: number, at: number): void => {
    uTime.value = at
  })

  return mesh
}
